import { ANONYMOUS_VIEWER, type Currency, type PriceViewer, type Product } from "@nh/shared";
import { priceFor, type ResolvedPrice } from "@/features/shop/shop.catalog";
import type { CartLine } from "./cart.model";

/**
 * Resolver el carrito contra el CATÁLOGO.
 *
 * Aquí es donde se paga —y se cobra— la decisión de `cart.model.ts` de no
 * guardar precios: una línea del almacenamiento es solo un identificador
 * y una cantidad, así que para pintarla hay que emparejarla con el
 * producto que acaba de llegar del servidor. Si el catálogo subió de
 * precio entre una visita y otra, el carrito enseña el precio NUEVO sin
 * hacer nada especial. Es la propiedad que hace que esto sea correcto.
 *
 * Funciones puras: se prueban con un array de líneas y un array de
 * productos, sin montar la pantalla ni tocar la red.
 */

export interface CartEntry {
  line: CartLine;
  /**
   * El producto del catálogo. `undefined` cuando ya no está publicado —
   * se despublicó, se archivó o se borró mientras el carrito esperaba en
   * el navegador de alguien.
   */
  product: Product | undefined;
  /** El precio en la moneda activa, o la caída que resuelve `priceFor`. */
  price: ResolvedPrice | null;
}

/**
 * Empareja cada línea con su producto, en el orden en que se añadieron.
 *
 * El índice por `id` se construye una vez: emparejar con `find` dentro
 * del bucle es cuadrático, y aunque hoy el catálogo sean veinte
 * productos, esto corre en cada render del panel.
 */
export function resolveCart(
  lines: readonly CartLine[],
  products: readonly Product[],
  currency: Currency,
  viewer: PriceViewer = ANONYMOUS_VIEWER,
): CartEntry[] {
  const byId = new Map(products.map((product) => [product.id, product]));

  return lines.map((line) => {
    const product = byId.get(line.productId);
    return {
      line,
      product,
      price: product ? priceFor(product, currency, viewer) : null,
    };
  });
}

/** Una línea cuenta para el subtotal solo si tiene precio EN ESA moneda. */
export function isCountable(entry: CartEntry, currency: Currency): boolean {
  return entry.product !== undefined && entry.price !== null && entry.price.currency === currency;
}

export interface CartSubtotal {
  cents: number;
  currency: Currency;
  /** Artículos que sí entraron en la suma. */
  countedItems: number;
  /**
   * Artículos que quedaron FUERA: sin producto en el catálogo, sin precio
   * capturado, o con precio solo en la otra moneda.
   */
  skippedItems: number;
}

/**
 * El subtotal, en centavos y en UNA sola moneda.
 *
 * ── Por qué hay artículos que no se suman ────────────────────────────
 * Un producto puede venderse solo en dólares (`priceMapSchema` tiene las
 * dos monedas opcionales). Sumar sus centavos a un total en pesos daría
 * un número inventado — el error clásico de tratar los centavos como si
 * la moneda fuera un adorno. Esas líneas se cuentan aparte y el panel lo
 * dice, en vez de mentir con un total redondo.
 *
 * ── Y por qué en centavos ────────────────────────────────────────────
 * La suma es exacta. El formato lo hace `formatPrice` al pintar, una sola
 * vez, y no aquí: dividir entre 100 en mitad de una acumulación es como
 * se acaba con un total que no cuadra con la suma de las filas.
 */
export function subtotalOf(
  entries: readonly CartEntry[],
  currency: Currency,
): CartSubtotal {
  let cents = 0;
  let countedItems = 0;
  let skippedItems = 0;

  for (const entry of entries) {
    if (isCountable(entry, currency) && entry.price) {
      cents += entry.price.cents * entry.line.quantity;
      countedItems += entry.line.quantity;
    } else {
      skippedItems += entry.line.quantity;
    }
  }

  return { cents, currency, countedItems, skippedItems };
}
